import React, { useMemo, useState, useEffect } from 'react';
import './Home.css';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';
import { useAppContext } from '../../AppContext';
import { 
  getDashboardData,
  formatSummariesForChart,
  calculateTotalSales
} from '../../Services/Summary.service';
import { Summary } from '../../Types/Summary';
import { toast, ToastContainer } from 'react-toastify';

const HomePage: React.FC = () => {
  const { theme } = useTheme();
  const { commerce } = useAppContext();
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [historicSummary, setHistoricSummary] = useState<Summary | null>(null);
  const [monthlySummaries, setMonthlySummaries] = useState<Summary[]>([]);
  const [salesData, setSalesData] = useState<{ day: string; sales: number }[]>([]);

  // Define colors based on current theme directly
  const chartColors = useMemo(() => {
    if (theme === 'dark') {
      return {
        line: '#fdf050', // biyuyo yellow
        grid: '#404040',
        axis: '#b3b3b3',
        text: '#ffffff',
        background: '#2d2d2d'
      };
    } else {
      return {
        line: '#000000', // biyuyo black
        grid: '#e0e0e0',
        axis: '#666666',
        text: '#333333',
        background: '#ffffff'
      };
    }
  }, [theme]);

  // Calculate totals
  const totalSalesEver = useMemo(() => {
    return historicSummary?.sales_amount || 0;
  }, [historicSummary]);

  const totalSalesThisMonth = useMemo(() => {
    return calculateTotalSales(monthlySummaries);
  }, [monthlySummaries]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!commerce?.id) {
        setError('No se encontró información del comercio');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch dashboard data (historic summary + current month summaries)
        const dashboardData = await getDashboardData();
        
        setHistoricSummary(dashboardData.historicSummary);
        setMonthlySummaries(dashboardData.currentMonthSummaries);

        // Transform data for chart
        const chartData = formatSummariesForChart(dashboardData.currentMonthSummaries);
        setSalesData(chartData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Error al cargar los datos de resumen');
        toast.error('Error al cargar los datos de resumen');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [commerce?.id]);

  if (isLoading) {
    return (
      <div className="home-container">
        <div className="loading">Cargando datos...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="home-container">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-container">
      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <h3>Total de Ventas</h3>
          <div className="amount">Bs.{totalSalesEver.toLocaleString()}</div>
          <p>Histórico total</p>
        </div>
        <div className="summary-card">
          <h3>Ventas del Mes</h3>
          <div className="amount">Bs.{totalSalesThisMonth.toLocaleString()}</div>
          <p>Mes actual</p>
        </div>
      </div>

      {/* Chart */}
      <div className="chart-container">
        <h2>Ventas por día - Mes actual</h2>
        {salesData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={salesData}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
              <XAxis 
                dataKey="day" 
                tick={{ fill: chartColors.axis, fontSize: 12 }}
                axisLine={{ stroke: chartColors.grid }}
                tickLine={{ stroke: chartColors.grid }}
              />
              <YAxis 
                tick={{ fill: chartColors.axis, fontSize: 12 }}
                axisLine={{ stroke: chartColors.grid }}
                tickLine={{ stroke: chartColors.grid }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: chartColors.background,
                  border: `1px solid ${chartColors.grid}`,
                  borderRadius: '8px',
                  color: chartColors.text
                }}
                formatter={(value: number) => [`Bs.${value.toLocaleString()}`, 'Ventas']}
              />
              <Line 
                type="monotone" 
                dataKey="sales" 
                stroke={chartColors.line} 
                strokeWidth={2} 
                dot={{ r: 5, fill: chartColors.line }} 
                activeDot={{ r: 7, fill: chartColors.line }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="no-data">
            <p>No hay datos de ventas para mostrar en este mes</p>
          </div>
        )}
      </div>
      
      <ToastContainer />
    </div>
  );
}

export default HomePage;